import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DndContext, DragOverlay, closestCenter } from "@dnd-kit/core";
import { toast } from "sonner";
import { ElementToolbox } from "@/components/designer/ElementToolbox";
import { PropertiesPanel } from "@/components/designer/PropertiesPanel";
import { CodeExportDialog } from "@/components/designer/CodeExportDialog";
import { PrintPreview } from "@/components/designer/PrintPreview";
import { SectionDropZone } from "@/components/designer/SectionDropZone";
import { DesignerHeader } from "@/components/designer/DesignerHeader";
import {
  ELEMENT_TYPES,
  DEFAULT_PAGE_SETTINGS,
  SECTION_ORDER,
  createDefaultSections,
  getPageSize,
} from "@/lib/elements";
import { templateApi, customElementApi, elementDefaultsApi, snippetApi } from "@/lib/api";
import { useDesignerCallbacks } from "./designer/useDesignerCallbacks";

/**
 * Small floating preview shown while a toolbox element / canvas element is
 * being dragged. Pure presentation — receives the active drag item from
 * dnd-kit and resolves a human label from it.
 */
function DragOverlayPreview({ activeDragItem }) {
  if (!activeDragItem) return null;
  const d = activeDragItem.data.current;
  let label = "Moving element";
  if (d?.fromToolbox) {
    if (d.elementType === "snippet") label = `★ ${d.snippetDef?.name || "Snippet"}`;
    else if (d.elementType === "custom") label = d.customElementDef?.name || "Custom";
    else label = ELEMENT_TYPES[d.elementType]?.label || label;
  }
  return (
    <div className="drag-overlay-item">
      <span className="text-sm font-medium text-blue-600">{label}</span>
    </div>
  );
}

/** Big centered placeholder shown when the canvas has zero elements. */
function EmptyCanvasPlaceholder() {
  return (
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
      <div className="text-center">
        <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
          <svg className="w-7 h-7 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v16m8-8H4" />
          </svg>
        </div>
        <p className="text-sm font-medium text-gray-400 mb-1">Drop elements into sections</p>
        <p className="text-xs text-gray-300">Drag components from the left panel</p>
      </div>
    </div>
  );
}

/** Top-right page-size badge on the canvas. */
function PageSizeBadge({ pageSettings }) {
  const sz = getPageSize(pageSettings.pageSize, pageSettings);
  const orient = sz.orientation === "landscape" ? " · L" : " · P";
  const label = pageSettings.pageSize === "Custom"
    ? `${sz.widthMM}×${sz.heightMM}mm`
    : pageSettings.pageSize;
  return (
    <div
      data-testid="canvas-page-size-badge"
      className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-[10px] font-medium tracking-wide pointer-events-none select-none"
      style={{
        background: "rgba(17, 24, 39, 0.05)",
        color: "#6B7280",
        border: "1px solid rgba(17, 24, 39, 0.06)",
        backdropFilter: "blur(2px)",
        zIndex: 5,
      }}
    >
      {label + orient}
    </div>
  );
}

export default function DesignerPage() {
  const { templateId } = useParams();
  const navigate = useNavigate();

  // ── Core editor state ─────────────────────────────────────────────────
  const [sections, setSections] = useState(createDefaultSections);
  const [selectedIds, setSelectedIds] = useState([]);
  const [templateName, setTemplateName] = useState("Untitled Invoice");
  const [currentTemplateId, setCurrentTemplateId] = useState(templateId || null);
  const [pageSettings, setPageSettings] = useState(DEFAULT_PAGE_SETTINGS);
  const [showCodeExport, setShowCodeExport] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [customElements, setCustomElements] = useState([]);
  const [snippets, setSnippets] = useState([]);
  const elementDefaultsRef = useRef({});

  /* Single primary id used by the per-element editor when only one is selected. */
  const selectedId = selectedIds.length === 1 ? selectedIds[0] : null;

  // ── Selection ──────────────────────────────────────────────────────────
  const handleSelectElement = useCallback((id, multi = false) => {
    if (!id) { setSelectedIds([]); return; }
    if (multi) {
      setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
    } else {
      setSelectedIds([id]);
    }
  }, []);

  // ── Bundled callbacks (CRUD + snippets + DnD) ─────────────────────────
  const cbs = useDesignerCallbacks({
    sections,
    setSections,
    selectedIds,
    setSelectedIds,
    setSnippets,
    elementDefaultsRef,
  });

  // ── Derived data ───────────────────────────────────────────────────────
  const allElements = useMemo(
    () => SECTION_ORDER.flatMap((key) => sections[key]),
    [sections]
  );
  const selectedElement = allElements.find((el) => el.id === selectedId) || null;
  const elementSection = selectedId ? cbs.findSectionForId(selectedId) : null;
  const totalElements = allElements.length;

  // ── Initial load: custom elements + snippets + element defaults ──────
  useEffect(() => {
    customElementApi.list().then(setCustomElements).catch(() => {});
    snippetApi.list().then(setSnippets).catch(() => {});
    elementDefaultsApi.list().then((items) => {
      const map = {};
      items.forEach((item) => { map[item.type] = item.overrides; });
      elementDefaultsRef.current = map;
    }).catch(() => {});
  }, []);

  // ── Load template by id ──────────────────────────────────────────────
  useEffect(() => {
    if (templateId) {
      templateApi
        .get(templateId)
        .then((template) => {
          setTemplateName(template.name);
          if (template.sections) {
            setSections({ ...createDefaultSections(), ...template.sections });
          } else {
            setSections({ ...createDefaultSections(), body: template.elements || [] });
          }
          setPageSettings(template.pageSettings || DEFAULT_PAGE_SETTINGS);
          setCurrentTemplateId(template.id);
        })
        .catch(() => {
          toast.error("Failed to load template");
          navigate("/");
        });
    }
  }, [templateId, navigate]);

  // ── Save ───────────────────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      const data = { name: templateName, sections, pageSettings };
      if (currentTemplateId) {
        await templateApi.update(currentTemplateId, data);
        toast.success("Template saved");
      } else {
        const created = await templateApi.create(data);
        setCurrentTemplateId(created.id);
        navigate(`/designer/${created.id}`, { replace: true });
        toast.success("Template created");
      }
    } catch {
      toast.error("Failed to save template");
    }
    setIsSaving(false);
  }, [templateName, sections, pageSettings, currentTemplateId, navigate]);

  // ── Global keyboard shortcuts: Delete/Backspace + Cmd/Ctrl-S ─────────
  useEffect(() => {
    const handler = (e) => {
      // Exclude key events fired from any editable surface, including
      // TipTap's ProseMirror contenteditable region. Without this, the
      // global Backspace handler deletes the whole element while the
      // user is just trying to delete one character of rich-text content.
      const t = e.target;
      const isEditable =
        t.tagName === "INPUT" ||
        t.tagName === "TEXTAREA" ||
        t.tagName === "SELECT" ||
        t.isContentEditable ||
        t.closest?.("[contenteditable='true']") ||
        t.closest?.(".ProseMirror");

      if (
        (e.key === "Delete" || e.key === "Backspace") &&
        selectedIds.length > 0 &&
        !isEditable
      ) {
        if (selectedIds.length > 1) {
          cbs.deleteSelectedElements();
        } else {
          cbs.deleteElement(selectedIds[0]);
        }
      }
      if (e.key === "s" && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        handleSave();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [selectedIds, cbs, handleSave]);

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <div className="h-screen flex flex-col bg-[#F9FAFB]" data-testid="designer-page">
      <DesignerHeader
        templateName={templateName}
        onTemplateNameChange={setTemplateName}
        totalElements={totalElements}
        isSaving={isSaving}
        onPreview={() => setShowPreview(true)}
        onExportEjs={() => setShowCodeExport(true)}
        onSave={handleSave}
      />

      <DndContext
        sensors={cbs.sensors}
        collisionDetection={closestCenter}
        onDragStart={cbs.handleDragStart}
        onDragEnd={cbs.handleDragEnd}
      >
        <div className="flex flex-1 overflow-hidden">
          <ElementToolbox
            customElements={customElements}
            snippets={snippets}
            onDeleteSnippet={cbs.handleDeleteSnippet}
          />

          <div
            className="flex-1 overflow-auto designer-canvas-wrapper p-8"
            onClick={(e) => {
              if (e.target === e.currentTarget) setSelectedIds([]);
            }}
          >
            <div
              className="bg-white shadow-xl mx-auto relative"
              data-testid="invoice-canvas"
              style={{
                width: getPageSize(pageSettings.pageSize, pageSettings).widthPx,
                minHeight: getPageSize(pageSettings.pageSize, pageSettings).heightPx,
                padding: "20mm",
              }}
              onClick={(e) => {
                if (e.target === e.currentTarget) setSelectedIds([]);
              }}
            >
              <PageSizeBadge pageSettings={pageSettings} />

              {SECTION_ORDER.map((sectionKey) => (
                <SectionDropZone
                  key={sectionKey}
                  sectionKey={sectionKey}
                  elements={sections[sectionKey]}
                  selectedIds={selectedIds}
                  onSelectElement={handleSelectElement}
                  onDeleteElement={cbs.deleteElement}
                  onDuplicateElement={cbs.duplicateElement}
                />
              ))}

              {totalElements === 0 && <EmptyCanvasPlaceholder />}
            </div>
          </div>

          <PropertiesPanel
            element={selectedElement}
            elementSection={elementSection}
            selectedIds={selectedIds}
            onMoveToSection={(target) => selectedId && cbs.moveElementToSection(selectedId, target)}
            onUpdate={(newProps) => selectedId && cbs.updateElement(selectedId, newProps)}
            onDelete={() => selectedId && cbs.deleteElement(selectedId)}
            onDuplicate={() => selectedId && cbs.duplicateElement(selectedId)}
            onSaveAsSnippet={() => selectedElement && cbs.handleSaveAsSnippet(selectedElement, elementSection)}
            onBulkDelete={cbs.deleteSelectedElements}
            onBulkDuplicate={cbs.duplicateSelectedElements}
            onBulkSaveAsSnippet={cbs.handleSaveSelectionAsSnippet}
            onClearSelection={() => setSelectedIds([])}
            pageSettings={pageSettings}
            onUpdatePageSettings={setPageSettings}
            sections={sections}
            customElements={customElements}
          />
        </div>

        <DragOverlay>
          <DragOverlayPreview activeDragItem={cbs.activeDragItem} />
        </DragOverlay>
      </DndContext>

      <CodeExportDialog
        open={showCodeExport}
        onOpenChange={setShowCodeExport}
        sections={sections}
        pageSettings={pageSettings}
      />

      {showPreview && (
        <PrintPreview
          sections={sections}
          pageSettings={pageSettings}
          onClose={() => setShowPreview(false)}
        />
      )}
    </div>
  );
}
